// AiHelpPanel — slide-in panel for the EmailComposer's AI help
// button. Phase 10b.

import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";

import {
  api,
  ApiError,
  type ComposeAssistInput,
  type ComposeMode,
  type ComposeThreadMessage,
} from "../../api";
import { Button, ErrorBanner } from "../../ui";

const MODES: Array<{ value: ComposeMode; label: string; needsDraft: boolean }> = [
  { value: "draft", label: "Draft", needsDraft: false },
  { value: "reply", label: "Reply", needsDraft: false },
  { value: "rewrite", label: "Rewrite", needsDraft: true },
  { value: "shorten", label: "Shorten", needsDraft: true },
  { value: "tone_warmer", label: "Warmer", needsDraft: true },
  { value: "tone_formal", label: "Formal", needsDraft: true },
];

export interface AiHelpPanelProps {
  // Current draft body — used for rewrite/shorten/tone modes.
  draft: string;
  // Recipient + household context for the system prompt.
  recipientName?: string | null;
  recipientEmail?: string | null;
  householdName?: string | null;
  // v1.x phase 11.11: when composing to multiple people, the host
  // passes the additional recipients here. The AI uses each
  // recipient's contextual data to address them by name. Capped at
  // 5 by the route — larger recipient sprays should go through the
  // bulk SES path.
  additionalRecipients?: Array<{
    name?: string | null;
    email?: string | null;
    householdName?: string | null;
  }>;
  // v1.x: prior messages in the same conversation, oldest first.
  // When present, the panel ships them to the AI prompt + shows a
  // small "Reading {N} messages in thread" indicator so the user
  // knows the model has the context.
  threadMessages?: ComposeThreadMessage[];
  // Alternative way to populate threadMessages: pass a Microsoft
  // conversationId and the panel will fetch + trim the thread on
  // open. Cheaper integration for callers that don't want to
  // pre-fetch.
  conversationId?: string | null;
  // For reply mode, the immediate message we're replying to —
  // surfaced to the model separately from `threadMessages` so the
  // mode-specific reply rendering (Subject + > body) still fires.
  inReplyToSubject?: string | null;
  inReplyToBodyPreview?: string | null;
  // Called when the advisor accepts a suggestion. The panel sends
  // the markup; the parent decides how to merge into its editor.
  onAccept: (suggestion: string) => void;
  // Called when the panel should close (cancel).
  onClose: () => void;
}

// Strip HTML to a flat text excerpt suitable for the model prompt.
// Same approach as style-anchor.ts (server-side) — keep client +
// server in shape so excerpts look consistent regardless of which
// side built them.
function htmlToText(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<\/(p|div|br|li)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/?[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function AiHelpPanel({
  draft,
  recipientName,
  recipientEmail,
  householdName,
  additionalRecipients,
  threadMessages: threadMessagesProp,
  conversationId,
  inReplyToSubject,
  inReplyToBodyPreview,
  onAccept,
  onClose,
}: AiHelpPanelProps) {
  // Locally-fetched thread (when conversationId is supplied and the
  // caller didn't already pre-fetch). Falls back to the prop value
  // when present.
  const [fetchedThread, setFetchedThread] = useState<
    ComposeThreadMessage[] | null
  >(null);
  const threadMessages = threadMessagesProp ?? fetchedThread ?? undefined;

  useEffect(() => {
    if (threadMessagesProp) return; // already provided
    if (!conversationId) return;
    let cancelled = false;
    void (async () => {
      try {
        const resp = await api.getEmailConversation(conversationId);
        if (cancelled) return;
        // The conversation endpoint returns previews only (no full
        // body) — bodyPreview is Microsoft's first-~250-chars
        // truncation, plenty for "what was said" context. Run it
        // through htmlToText anyway in case Graph returns HTML
        // entities.
        const trimmed = resp.messages
          .map((m) => ({
            fromName: m.fromName ?? null,
            fromEmail: m.fromAddress ?? null,
            receivedDateTime: m.receivedDateTime ?? null,
            bodyExcerpt: htmlToText(m.bodyPreview ?? "").slice(0, 600),
          }))
          .filter((m) => m.bodyExcerpt.length > 0)
          .slice(-5); // most recent 5
        setFetchedThread(trimmed);
      } catch {
        // Best effort. AI compose still works without thread context.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId, threadMessagesProp]);
  // Default to "reply" mode when there's a reply context — the host
  // is most likely invoking us to draft a reply, and pre-selecting
  // the right mode saves a click. Falls back to "draft" otherwise.
  const initialMode: ComposeMode =
    inReplyToBodyPreview || (threadMessages && threadMessages.length > 0)
      ? "reply"
      : "draft";
  const [mode, setMode] = useState<ComposeMode>(initialMode);
  const [instruction, setInstruction] = useState("");
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokens, setTokens] = useState<{ total: number } | null>(null);

  const generate = useCallback(async () => {
    if (instruction.trim().length === 0) return;
    setLoading(true);
    setError(null);
    setSuggestion(null);
    try {
      const input: ComposeAssistInput = {
        mode,
        instruction: instruction.trim(),
        ...(MODES.find((m) => m.value === mode)?.needsDraft ? { draft } : {}),
        context: {
          recipientName: recipientName ?? null,
          recipientEmail: recipientEmail ?? null,
          householdName: householdName ?? null,
          ...(additionalRecipients && additionalRecipients.length > 0
            ? { additionalRecipients }
            : {}),
          ...(inReplyToSubject ? { inReplyToSubject } : {}),
          ...(inReplyToBodyPreview ? { inReplyToBodyPreview } : {}),
          ...(threadMessages && threadMessages.length > 0
            ? { threadMessages }
            : {}),
        },
      };
      const resp = await api.composeAssist(input);
      setSuggestion(resp.suggestion);
      setTokens({ total: resp.tokens_used.total });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "AI compose failed.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [
    mode,
    instruction,
    draft,
    recipientName,
    recipientEmail,
    householdName,
    additionalRecipients,
    threadMessages,
    inReplyToSubject,
    inReplyToBodyPreview,
  ]);

  const accept = useCallback(async () => {
    if (!suggestion) return;
    onAccept(suggestion);
    // v1.x phase 11.16 (Tier B item 14) — pass mode so the
    // per-mode auto-tune counters in user_ai_preferences update
    // alongside the global counts.
    void api.submitComposeFeedback(true, mode).catch(() => {
      // Best effort — accepted-state already in UI.
    });
  }, [suggestion, onAccept, mode]);

  const reject = useCallback(async () => {
    setSuggestion(null);
    setTokens(null);
    void api.submitComposeFeedback(false, mode).catch(() => {
      // best effort
    });
  }, [mode]);

  return (
    <div className="ai-help-panel">
      <header className="ai-help-panel-header">
        <h3>AI help</h3>
        <button
          type="button"
          className="drawer-close"
          onClick={onClose}
          aria-label="Close AI help"
        >
          <X size={16} strokeWidth={1.5} aria-hidden />
        </button>
      </header>
      <div className="ai-help-panel-body">
        {error ? <ErrorBanner message={error} /> : null}

        {threadMessages && threadMessages.length > 0 ? (
          <p className="ai-help-thread-indicator" title="The AI sees these prior messages.">
            Reading {threadMessages.length} prior{" "}
            {threadMessages.length === 1 ? "message" : "messages"} in thread.
          </p>
        ) : null}

        <div className="ai-help-mode-row">
          {MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              className={`ai-help-mode-pill${mode === m.value ? " is-active" : ""}`}
              onClick={() => setMode(m.value)}
            >
              {m.label}
            </button>
          ))}
        </div>

        <label className="ai-help-instruction-label">
          What should the AI do?
          <textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder={
              mode === "draft"
                ? "e.g. Draft a friendly check-in for Mary about her March renewal."
                : "e.g. Make this warmer. Mention Brian by name."
            }
            rows={3}
          />
        </label>

        <div className="ai-help-cta-row">
          <Button
            variant="primary"
            onClick={() => void generate()}
            disabled={loading || instruction.trim().length === 0}
          >
            {loading ? "Generating…" : "Generate"}
          </Button>
        </div>

        {suggestion ? (
          <section className="ai-help-suggestion">
            <header
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 4,
              }}
            >
              <strong style={{ fontSize: 12 }}>Suggestion</strong>
              {tokens ? (
                <span className="muted" style={{ fontSize: 11 }}>
                  ~{tokens.total} tokens
                </span>
              ) : null}
            </header>
            <div className="ai-help-suggestion-body">
              {suggestion.split("\n").map((line, i) => (
                <p key={i} style={{ margin: "0 0 8px" }}>
                  {line}
                </p>
              ))}
            </div>
            <div className="ai-help-cta-row" style={{ marginTop: 8 }}>
              <Button variant="primary" onClick={() => void accept()}>
                Accept
              </Button>
              <Button variant="ghost" onClick={() => void reject()}>
                Reject
              </Button>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
